import { graphQlClient } from '@config/graphqlClient';
import CrewsGraphQL from '@graphql/crew.queries';
import { Crew } from '@models/crew.model';
import { Task } from '@models/task.model';

export const crewService = {
  async createCrew(name: string): Promise<Crew> {
    const payload = { crew: { name } } as any;
    const data: any = await graphQlClient.request(
      CrewsGraphQL.mutations.createCrew,
      payload,
    );
    return data.createCrew as Crew;
  },

  async updateCrew(crewId: string, name: string): Promise<Crew> {
    const payload = { crew: { crewId, name } } as any;
    const data: any = await graphQlClient.request(
      CrewsGraphQL.mutations.updateCrew,
      payload,
    );
    return data.updateCrew as Crew;
  },

  async assignEmployeesToCrew(
    crewId: string,
    employeeIds: string[],
  ): Promise<Crew> {
    const data: any = await graphQlClient.request(
      CrewsGraphQL.mutations.assignEmployeesToCrew,
      { crewId, employeeIds },
    );
    return data.assignEmployeesToCrew as Crew;
  },

  async assignTasksToCrew(crewId: string, taskIds: string[]): Promise<Crew> {
    const data: any = await graphQlClient.request(
      CrewsGraphQL.mutations.assignTasksToCrew,
      { crewId, taskIds },
    );
    return data.assignTasksToCrew as Crew;
  },

  async getAssignableTasks(
    projectIds?: string[],
    includeCompleted = false,
  ): Promise<Task[]> {
    const data: any = await graphQlClient.request(
      CrewsGraphQL.queries.getAssignableTasks,
      { projectIds: projectIds?.length ? projectIds : null, includeCompleted },
    );
    return (data?.getAssignableTasks || []) as Task[];
  },

  async getCrewsByUser(userId: string): Promise<Crew[]> {
    const data: any = await graphQlClient.request(
      CrewsGraphQL.queries.getCrewsByUser,
      { userId },
    );
    return (data?.getCrewsByUser || []) as Crew[];
  },

  async getCrewById(crewId: string): Promise<Crew | null> {
    if (!crewId) throw new Error('crewId is required');
    try {
      const data: any = await graphQlClient.request(
        CrewsGraphQL.queries.getCrewById,
        { id: crewId },
      );

      const result = data?.getCrewById;
      if (!result) return null;
      if (Array.isArray(result)) {
        return (result[0] as Crew) || null;
      }
      return (result as Crew) || null;
    } catch (error) {
      throw error;
    }
  },

  async deleteCrew(crewId: string) {
    if (!crewId) throw new Error('crewId is required');
    const data: any = await graphQlClient.request(
      CrewsGraphQL.mutations.deleteCrew,
      { id: crewId },
    );
    return data.deleteCrew;
  },

  async unassignEmployee(crewId: string, employeeId: string): Promise<Crew> {
    const data: any = await graphQlClient.request(
      CrewsGraphQL.mutations.unassignEmployee,
      { crewId, employeeId },
    );
    return data.unassignEmployee as Crew;
  },
};

export default crewService;
